import type { ColumnFiltersState } from '@tanstack/react-table'
import {
  buildDocumentBrowseFacetQueries,
  type FacetFilterId,
  type FacetOption,
  parseDocumentBrowseFacetOptions,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/browseAllFacets'
import { scopedTagsIdentityKey } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/browseAllFilters'
import { useRolodexHistogram } from 'features/rolodex'
import { useMemo, useRef } from 'react'

export function useDocumentBrowseFacets(
  scopedTags: Record<string, string | string[]> | undefined,
  activeFilters: ColumnFiltersState | undefined
): {
  isLoadingFacets: boolean
  optionsByFilterId: Partial<Record<FacetFilterId, FacetOption[]>>
} {
  const queries = useMemo(
    () => buildDocumentBrowseFacetQueries(scopedTags, activeFilters),
    [activeFilters, scopedTags]
  )
  const { data, isLoading } = useRolodexHistogram(queries)
  const computedOptions = useMemo(
    () => parseDocumentBrowseFacetOptions(data),
    [data]
  )
  const lastGoodOptionsRef = useRef<
    Partial<Record<FacetFilterId, FacetOption[]>>
  >({})
  const scopeIdentity = scopedTagsIdentityKey(scopedTags)
  const scopeIdentityRef = useRef(scopeIdentity)
  if (scopeIdentityRef.current !== scopeIdentity) {
    scopeIdentityRef.current = scopeIdentity
    lastGoodOptionsRef.current = {}
  }
  if (computedOptions !== undefined) {
    lastGoodOptionsRef.current = computedOptions
  }

  return {
    isLoadingFacets: isLoading,
    optionsByFilterId: lastGoodOptionsRef.current,
  }
}
